import { styled } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import React from 'react';

import { DefaultSelectItem } from '@/components/molecules/DefaultSelectItem';
import { SelectItem } from '@/types/fields';

interface Props<ItemValueType extends string | number = string> {
  readonly name?: string;
  readonly label?: string;
  readonly items: readonly SelectItem<ItemValueType>[];
  readonly defaultValue?: ItemValueType | '';
  readonly value?: ItemValueType | '';

  renderItem?(item: SelectItem<ItemValueType>): React.ReactNode;
  onChange?(item: ItemValueType | ''): void;
}

export function StaticItemsCustomSelect<ItemValueType extends string | number = string>(
  props: Props<ItemValueType>,
): React.ReactElement {
  const { name, label, items, defaultValue, value, onChange } = props;
  const { renderItem = defaultItemRenderer } = props;

  const nonNullValue = React.useMemo(
    (): ItemValueType | '' | undefined => (value === null ? '' : value),
    [value],
  );

  const nonNullDefaultValue = React.useMemo(
    (): ItemValueType | '' | undefined => (defaultValue === null ? '' : defaultValue),
    [defaultValue],
  );

  const onChangeWrapper = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { value } = event.target;
      onChange?.(value as ItemValueType);
    },
    [onChange],
  );

  return (
    <TextField
      variant="outlined"
      select={true}
      name={name}
      value={nonNullValue}
      defaultValue={nonNullDefaultValue}
      label={label}
      fullWidth={true}
      disabled={items.length === 0}
      onChange={onChangeWrapper}>
      {/* Ensure current value is "always" valid */}
      {items.length === 0 ? (
        <EmptyItem disabled={true} value={nonNullValue ?? nonNullDefaultValue} />
      ) : null}

      {items.map(
        (item: SelectItem<ItemValueType>): React.ReactElement => (
          <MenuItem key={item.value} role="option" value={item.value}>
            {renderItem(item)}
          </MenuItem>
        ),
      )}
    </TextField>
  );
}

function defaultItemRenderer<T extends string | number = string>(
  item: SelectItem<T>,
): React.ReactNode {
  return <DefaultSelectItem key={item.value} item={item} />;
}

const EmptyItem = styled(MenuItem)({
  height: 0,
});
